import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useData } from '../context/DataContext';
import { SiteData } from '../types';
import { createClient } from '@supabase/supabase-js';
import { SiteDetailModal } from './SiteDetailModal';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { 
  Search, 
  Bot, 
  FileText, 
  Calendar, 
  ExternalLink,
  TrendingUp,
  Activity,
  AlertTriangle,
  CheckCircle,
  Sparkles,
  Users,
  Globe,
  Server,
  Clock,
  Lightbulb,
  ThumbsUp,
  ThumbsDown,
  MessageCircle,
  Trash2
} from 'lucide-react';

interface AIResponse {
  answer: string;
  relevantData?: any[];
  suggestions: Array<{
    type: 'report' | 'calendar' | 'crm' | 'analysis';
    title: string;
    description: string;
    action?: () => void;
  }>;
  usedWebSearch?: boolean;
  visualSummary?: any;
  queryId?: string; // For feedback tracking
}

interface FeedbackData {
  queryId: string;
  query: string;
  response: string;
  rating: 'positive' | 'negative';
  comment?: string;
  timestamp: number;
}

// Enhanced fuzzy matching with Levenshtein distance
const levenshteinDistance = (str1: string, str2: string): number => {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
  
  for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
  
  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,
        matrix[j - 1][i] + 1,
        matrix[j - 1][i - 1] + indicator
      );
    }
  }
  
  return matrix[str2.length][str1.length];
};

const advancedFuzzyMatch = (text: string, searchTerm: string, threshold: number = 0.7): boolean => {
  const textLower = text.toLowerCase();
  const searchLower = searchTerm.toLowerCase();
  
  // Exact match
  if (textLower.includes(searchLower)) return true;
  
  // Check each word in the search term
  const searchWords = searchLower.split(' ');
  const textWords = textLower.split(' ');
  
  return searchWords.some(searchWord => {
    if (searchWord.length < 3) return textLower.includes(searchWord);
    
    return textWords.some(textWord => {
      if (textWord.length < 3) return false;
      const distance = levenshteinDistance(textWord, searchWord);
      const similarity = 1 - (distance / Math.max(textWord.length, searchWord.length));
      return similarity >= threshold;
    });
  });
};

// Dynamic query suggestions - no longer need static list

// Visual Summary Components
const StatWidget = ({ icon: Icon, label, value, subValue = "", color = "text-primary" }: {
  icon: any;
  label: string;
  value: string | number;
  subValue?: string;
  color?: string;
}) => (
  <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
    <Icon className={`w-8 h-8 ${color}`} />
    <div>
      <div className="text-lg font-bold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
      {subValue && <div className="text-xs text-muted-foreground">{subValue}</div>}
    </div>
  </div>
);

const MiniBarChart = ({ data, title }: { data: any[]; title: string }) => (
  <div className="h-32 w-full">
    <div className="text-xs font-medium mb-2">{title}</div>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data}>
        <XAxis dataKey="name" tick={{ fontSize: 10 }} />
        <YAxis tick={{ fontSize: 10 }} />
        <Bar dataKey="value" fill="#4F46E5" radius={[2, 2, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const StatusPieChart = ({ data, title }: { data: any[]; title: string }) => {
  const COLORS = ['#10B981', '#EF4444', '#F59E0B'];
  
  return (
    <div className="h-32 w-full">
      <div className="text-xs font-medium mb-2">{title}</div>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={20}
            outerRadius={40}
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

// Enhanced Query Intent Recognition System with Site/Customer Detection
const analyzeQueryIntent = (query: string, siteData: SiteData[]) => {
  const lowerQuery = query.toLowerCase().trim();
  console.log(`🔍 Analyzing query: "${lowerQuery}"`);
  console.log(`📊 Total sites available: ${siteData.length}`);
  
  // First, check for site names using STRICT matching (no fuzzy logic)
  const findBestSiteMatch = (searchTerm: string) => {
    let bestMatch = null;
    let bestScore = 0;
    const queryLower = searchTerm.toLowerCase().trim();
    
    console.log(`🔍 STRICT SEARCH for: "${queryLower}"`);
    console.log(`📊 Searching through ${siteData.length} sites`);
    
    // Log first few sites for debugging
    console.log(`📋 First 3 sites in data:`, siteData.slice(0, 3).map(s => `${s.siteName} (${s.customer})`));
    
    siteData.forEach((site, index) => {
      const siteName = site.siteName.toLowerCase().trim();
      const customer = site.customer.toLowerCase().trim();
      const country = site.country.toLowerCase().trim();
      const region = site.region ? site.region.toLowerCase().trim() : '';
      
      let score = 0;
      let matchType = '';
      
      // Debug log for sites that might be relevant to Cleveland searches
      if (siteName.includes('ccf') || customer.includes('cleveland')) {
        console.log(`🔍 [${index}] Checking: "${site.siteName}" | Customer: "${site.customer}"`);
        console.log(`    Lowercase: siteName="${siteName}", customer="${customer}"`);
        console.log(`    Query: "${queryLower}"`);
        console.log(`    Exact match check: customer "${customer}" === query "${queryLower}"? ${customer === queryLower}`);
        console.log(`    Contains match check: customer "${customer}".includes("${queryLower}")? ${customer.includes(queryLower)}`);
      }
      
      // PRIORITY 1: EXACT SITE NAME MATCH (100%)
      if (siteName === queryLower) {
        score = 1000;
        matchType = 'exact_site_name';
        console.log(`🎯 EXACT SITE NAME: ${site.siteName} matches "${queryLower}" (score: 1000)`);
      }
      // PRIORITY 2: EXACT CUSTOMER NAME MATCH (100%) 
      else if (customer === queryLower) {
        score = 900;
        matchType = 'exact_customer';
        console.log(`🎯 EXACT CUSTOMER: "${site.customer}" matches "${queryLower}" (score: 900)`);
      }
      // PRIORITY 3: EXACT COUNTRY MATCH (100%)
      else if (country === queryLower) {
        score = 800;
        matchType = 'exact_country';
        console.log(`🎯 EXACT COUNTRY: ${site.country} matches "${queryLower}" (score: 800)`);
      }
      // PRIORITY 4: EXACT REGION MATCH (100%)
      else if (region && region === queryLower) {
        score = 700;
        matchType = 'exact_region';
        console.log(`🎯 EXACT REGION: ${site.region} matches "${queryLower}" (score: 700)`);
      }
      // PRIORITY 5: MEANINGFUL PARTIAL CUSTOMER MATCH
      else if (customer.startsWith(queryLower) || 
              (customer.includes(queryLower) && queryLower.length >= 4)) {
        score = 600;
        matchType = 'partial_customer';
        console.log(`📍 PARTIAL CUSTOMER: "${site.customer}" contains "${queryLower}" (score: 600)`);
      }
      // PRIORITY 6: MEANINGFUL PARTIAL SITE NAME MATCH
      else if (siteName.startsWith(queryLower) || 
              (siteName.includes(queryLower) && queryLower.length >= 4)) {
        score = 500;
        matchType = 'partial_site_name';
        console.log(`📍 PARTIAL SITE NAME: "${site.siteName}" contains "${queryLower}" (score: 500)`);
      }
      
      // NO WORD-BY-WORD MATCHING - this prevents false positives
      
      if (score > bestScore) {
        bestScore = score;
        bestMatch = { site, matchType };
        console.log(`🏆 NEW BEST MATCH: ${site.siteName} (${site.customer}) - Score: ${score} - Type: ${matchType}`);
      }
    });
    
    if (bestMatch) {
      console.log(`🎯 FINAL STRICT MATCH: ${bestMatch.site.siteName} (${bestMatch.site.customer}) - ${bestMatch.matchType}, score: ${bestScore}`);
    } else {
      console.log(`❌ NO STRICT MATCH found for: "${queryLower}"`);
      console.log(`📋 Available customers with "cleveland": ${siteData.filter(s => s.customer.toLowerCase().includes('cleveland')).map(s => s.customer).join(', ')}`);
    }
    return bestMatch;
  };
  
  // Check for common site abbreviations and known patterns
  const siteAbbreviations = {
    'ccf': 'cleveland clinic',
    'mayo': 'mayo clinic', 
    'bwh': 'brigham',
    'jhh': 'johns hopkins',
    'mgh': 'mass general',
    'ucsf': 'university of california',
    'stanford': 'stanford',
    'harvard': 'harvard',
    'msr': 'msr', // Keep original for exact match
    'hiae': 'hiae',
    'duke': 'duke',
    'cooper': 'cooper',
    'aana': 'aana'
  };
  
  // Extract potential site/customer identifiers from query
  const fullQuery = lowerQuery;
  let detectedEntity = null;
  
  console.log(`🔍 STEP 1: Checking FULL QUERY: "${fullQuery}"`);
  
  // Remove all Carilion-related debugging that confuses search results
  const relevantSites = siteData.filter(site => 
    site.siteName.toLowerCase().includes('cleveland') || 
    site.customer.toLowerCase().includes('cleveland') ||
    site.siteName.toLowerCase().includes('ccf')
  );
  
  if (relevantSites.length > 0) {
    console.log(`🔍 CLEVELAND/CCF SITES FOUND in live data:`, relevantSites.map(s => ({
      siteName: s.siteName,
      customer: s.customer,
      country: s.country
    })));
  }
  
  // FIRST: Check the full query phrase for exact/partial matches
  const fullQueryMatch = findBestSiteMatch(fullQuery);
  if (fullQueryMatch) {
    console.log(`✅ FULL QUERY MATCH found: ${fullQueryMatch.site.siteName}`);
    detectedEntity = fullQueryMatch;
  } else {
    console.log(`❌ No full query match for: "${fullQuery}"`);
    
    // SECOND: Check abbreviations for the full query
    if (siteAbbreviations[fullQuery]) {
      console.log(`📋 Found abbreviation mapping: ${fullQuery} -> ${siteAbbreviations[fullQuery]}`);
      const abbrevMatch = findBestSiteMatch(siteAbbreviations[fullQuery]);
      if (abbrevMatch) {
        console.log(`✅ Abbreviation match successful for "${fullQuery}"`);
        detectedEntity = abbrevMatch;
      }
    }
    
    // THIRD: Only if no full query match, check individual words with strict criteria
    if (!detectedEntity) {
      console.log(`🔍 STEP 2: Checking individual words as fallback...`);
      const words = lowerQuery.split(/\s+/);
      
      for (const word of words) {
        console.log(`🔍 Checking individual word: "${word}"`);
        
        // Skip common words that aren't site names
        const skipWords = ['usage', 'status', 'activity', 'users', 'data', 'info', 'report', 'the', 'and', 'or', 'of', 'in', 'on', 'at', 'by', 'for', 'clinic', 'hospital', 'medical'];
        if (skipWords.includes(word)) {
          console.log(`⏭️ Skipping common word: "${word}"`);
          continue;
        }
        
        // Only match individual words that are likely to be unique identifiers
        if (word.length >= 6) { // Only longer, more unique words
          // Check abbreviations first
          if (siteAbbreviations[word]) {
            console.log(`📋 Found abbreviation mapping: ${word} -> ${siteAbbreviations[word]}`);
            const match = findBestSiteMatch(siteAbbreviations[word]);
            if (match) {
              console.log(`✅ Abbreviation match successful for "${word}"`);
              detectedEntity = match;
              break;
            }
          }
          
          // Check direct word matches with strict criteria
          const match = findBestSiteMatch(word);
          if (match) {
            console.log(`✅ Individual word match: "${word}" -> ${match.site.siteName}`);
            detectedEntity = match;
            break;
          }
        } else {
          console.log(`⏭️ Skipping short word: "${word}" (length ${word.length})`);
        }
      }
    }
  }
  
  if (detectedEntity) {
    console.log(`🎯 FINAL DETECTED ENTITY: ${detectedEntity.site.siteName} (${detectedEntity.matchType})`);
  } else {
    console.log(`❌ NO ENTITY DETECTED from query: "${lowerQuery}"`);
  }
  
  // Intent hierarchy (order matters - more specific first)
  interface IntentConfig {
    keywords?: string[];
    condition?: () => boolean;
    priority: number;
  }
  
  const intents: Record<string, IntentConfig> = {
    regionalBreakdown: {
      keywords: ['europe usage by country', 'apac usage by country', 'na usage by country', 'by country'],
      priority: 1
    },
    site: {
      condition: () => detectedEntity?.matchType === 'site',
      priority: 2
    },
    customer: {
      condition: () => detectedEntity?.matchType === 'customer',
      priority: 3
    },
    country: {
      keywords: ['france', 'japan', 'germany', 'usa', 'united states', 'canada', 'australia', 'singapore', 'china', 'india', 'uk', 'united kingdom', 'spain', 'italy', 'sweden', 'norway', 'denmark', 'netherlands', 'belgium', 'israel', 'brazil', 'mexico', 'south korea', 'korea', 'taiwan'],
      priority: 4
    },
    regional: {
      keywords: ['europe', 'apac', 'asia pacific', 'north america', 'na', 'emea'],
      priority: 5
    },
    general: {
      keywords: ['usage', 'status', 'performance', 'sites', 'online', 'offline'],
      priority: 6
    }
  };
  
  // Detect intent
  for (const [intentType, config] of Object.entries(intents)) {
    if (config.condition && config.condition()) {
      return {
        type: intentType,
        priority: config.priority,
        detectedEntity,
        keywords: []
      };
    }
    
    if (config.keywords) {
      const hasMatch = config.keywords.some(keyword => 
        lowerQuery.includes(keyword.toLowerCase()) || 
        advancedFuzzyMatch(lowerQuery, keyword, 0.8)
      );
      
      if (hasMatch) {
        return {
          type: intentType,
          priority: config.priority,
          detectedEntity,
          keywords: config.keywords.filter(k => lowerQuery.includes(k.toLowerCase()))
        };
      }
    }
  }
  
  return { 
    type: 'general', 
    priority: 6, 
    detectedEntity, 
    keywords: [] 
  };
};

// Enhanced Regional Filtering with Strict Boundaries
const getRegionalSites = (region: string, siteData: SiteData[]) => {
  const regionMappings = {
    'europe': {
      countries: ['france', 'germany', 'spain', 'italy', 'uk', 'united kingdom', 'britain', 'netherlands', 'belgium', 'austria', 'switzerland', 'sweden', 'norway', 'denmark', 'finland', 'poland', 'czech republic', 'hungary', 'romania', 'bulgaria', 'greece', 'portugal', 'ireland'],
      aliases: ['europe', 'eu', 'emea']
    },
    'apac': {
      countries: ['australia', 'singapore', 'japan', 'china', 'korea', 'south korea', 'india', 'thailand', 'malaysia', 'indonesia', 'philippines', 'vietnam', 'taiwan', 'hong kong', 'new zealand'],
      aliases: ['apac', 'asia pacific', 'asia', 'far east']
    },
    'north america': {
      countries: ['usa', 'united states', 'america', 'canada', 'mexico'],
      aliases: ['north america', 'na', 'america']
    }
  };
  
  // Find matching region
  const targetRegion = Object.entries(regionMappings).find(([regionName, config]) => 
    config.aliases.some(alias => region.includes(alias.toLowerCase()) || alias.toLowerCase().includes(region))
  );
  
  if (!targetRegion) return [];
  
  const [regionName, config] = targetRegion;
  
  // Filter sites strictly by country membership
  return siteData.filter(site => {
    const siteCountry = (site.country || '').toLowerCase();
    return config.countries.some(country => 
      siteCountry.includes(country.toLowerCase()) || 
      siteCountry === country.toLowerCase() ||
      // Handle special cases
      (country === 'usa' && (siteCountry.includes('united states') || siteCountry === 'us')) ||
      (country === 'united states' && (siteCountry.includes('usa') || siteCountry === 'us')) ||
      (country === 'uk' && (siteCountry.includes('united kingdom') || siteCountry.includes('britain')))
    );
  });
};

// Enhanced processQueryLocallyWithVisuals with Intent Recognition
const processQueryLocallyWithVisuals = async (inputQuery: string, siteData: SiteData[], simulationStats: any[]): Promise<AIResponse & { visualSummary?: any }> => {
  const query = inputQuery.toLowerCase();
  const queryId = Date.now().toString();
  
  console.log(`🔍 LOCAL PROCESSING - Query: "${inputQuery}"`);
  console.log(`📊 LOCAL PROCESSING - Site data length: ${siteData.length}`);
  console.log(`🎯 LOCAL PROCESSING - Analyzing query intent...`);
  
  // Analyze the query intent
  const intent = analyzeQueryIntent(inputQuery, siteData);
  console.log(`💡 LOCAL PROCESSING - Detected intent:`, intent);
  
  // Helper function to check for SITE + SIMULATOR combination
  const checkSiteSimulatorCombination = (searchQuery: string, sites: SiteData[], simStats: any[]) => {
    console.log(`Checking site + simulator combination for: "${searchQuery}"`);
    
    // First, try to detect a site
    let detectedSite = null;
    const queryWords = searchQuery.toLowerCase().split(/\s+/);
    
    // Check each word against site names (exact and fuzzy matching)
    for (const word of queryWords) {
      const siteMatch = sites.find(site => {
        const siteName = site.siteName.toLowerCase();
        return siteName === word || 
               siteName.includes(word) || 
               word.includes(siteName) ||
               advancedFuzzyMatch(siteName, word, 0.8);
      });
      
      if (siteMatch) {
        detectedSite = siteMatch;
        console.log(`Found site match: ${siteMatch.siteName} for word "${word}"`);
        break;
      }
    }
    
    if (!detectedSite) {
      console.log('No site detected in query');
      return { found: false };
    }
    
    // Now check for simulator types in the remaining words
    const availableSimulatorTypes = [...new Set(simStats.map(stat => stat.simulatorType).filter(type => type && type.trim() !== ''))];
    const matchedSimulatorTypes = [];
    
    // Check for simulator type matches
    for (const word of queryWords) {
      const simMatches = availableSimulatorTypes.filter(type => {
        const typeLower = type.toLowerCase();
        return typeLower.includes(word) || 
               word.includes(typeLower) ||
               advancedFuzzyMatch(typeLower, word, 0.7);
      });
      matchedSimulatorTypes.push(...simMatches);
    }
    
    // Remove duplicates
    const uniqueSimulatorTypes = [...new Set(matchedSimulatorTypes)];
    
    if (uniqueSimulatorTypes.length === 0) {
      console.log('No simulator type detected in query - returning site-only match');
      // Return site-only match for queries like "MSR usage" or "MSR status"
      return {
        found: true,
        site: detectedSite,
        simulatorTypes: [],
        stats: [],
        totalSessions: 0,
        siteOnly: true
      };
    }
    
    console.log(`Detected simulator types: ${uniqueSimulatorTypes.join(', ')} for site: ${detectedSite.siteName}`);
    
    // Filter simulation stats for this specific site + simulator combination
    const filteredStats = simStats.filter(stat => 
      stat.siteName === detectedSite.siteName && 
      uniqueSimulatorTypes.includes(stat.simulatorType)
    );
    
    const totalSessions = filteredStats.reduce((sum, stat) => sum + stat.sessionsLast12Months, 0);
    
    console.log(`Found ${filteredStats.length} simulation records with ${totalSessions} total sessions`);
    
    return {
      found: true,
      site: detectedSite,
      simulatorTypes: uniqueSimulatorTypes,
      stats: filteredStats,
      totalSessions
    };
  };
  
  // Helper function to detect simulator types
  const findSimulatorTypeMatches = (searchTerm: string) => {
    const availableSimulatorTypes = [...new Set(simulationStats.map(stat => stat.simulatorType).filter(type => type && type.trim() !== ''))];
    console.log('Available simulator types:', availableSimulatorTypes);
    
    // Enhanced simulator type detection - more precise matching
    const searchWords = searchTerm.toLowerCase().split(/\s+/);
    const matchedSimulatorTypes = [];
    
    // First, try to find exact simulator names or close matches
    for (const simulatorType of availableSimulatorTypes) {
      const typeLower = simulatorType.toLowerCase();
      const typeWords = typeLower.split(/\s+/);
      
      // Check if any significant word from search matches simulator type
      let hasMatch = false;
      for (const searchWord of searchWords) {
        // Skip common words like "usage", "sessions", "data", etc.
        if (['usage', 'sessions', 'data', 'show', 'me', 'in', 'the', 'for', 'with', 'mentor'].includes(searchWord)) {
          continue;
        }
        
        // Check for exact word matches or strong partial matches
        for (const typeWord of typeWords) {
          if (typeWord === searchWord || 
              (searchWord.length >= 4 && typeWord.includes(searchWord)) ||
              (typeWord.length >= 4 && searchWord.includes(typeWord)) ||
              advancedFuzzyMatch(typeWord, searchWord, 0.8)) {
            hasMatch = true;
            break;
          }
        }
        if (hasMatch) break;
      }
      
      if (hasMatch) {
        matchedSimulatorTypes.push(simulatorType);
        console.log(`✅ Matched simulator type: ${simulatorType} for search "${searchTerm}"`);
      }
    }
    
    // If no matches found with strict criteria, try looser matching but still be selective
    if (matchedSimulatorTypes.length === 0) {
      for (const simulatorType of availableSimulatorTypes) {
        const typeLower = simulatorType.toLowerCase();
        
        // Check if simulator type name appears in search term
        if (searchTerm.includes(typeLower) || 
            searchWords.some(word => word.length >= 4 && typeLower.includes(word))) {
          matchedSimulatorTypes.push(simulatorType);
          console.log(`⚠️ Loose match simulator type: ${simulatorType} for search "${searchTerm}"`);
        }
      }
    }
    
    if (matchedSimulatorTypes.length > 0) {
      console.log(`Found simulator types matching "${searchTerm}":`, matchedSimulatorTypes);
      
      // Filter simulation stats by matched simulator types ONLY
      const filteredStats = simulationStats.filter(stat => 
        matchedSimulatorTypes.includes(stat.simulatorType)
      );
      
      // Get unique sites from filtered simulation stats
      const siteNames = [...new Set(filteredStats.map(stat => stat.siteName))];
      const matchingSites = siteData.filter(site => siteNames.includes(site.siteName));
      
      return { simulatorTypes: matchedSimulatorTypes, sites: matchingSites, stats: filteredStats };
    }
    
    return null;
  };

  // Helper function to detect simulator + country combinations
  const findSimulatorCountryMatches = (searchQuery: string) => {
    console.log(`🎯 Checking for simulator + country combination in: "${searchQuery}"`);
    
    // First, detect simulator types with improved precision
    const availableSimulatorTypes = [...new Set(simulationStats.map(stat => stat.simulatorType).filter(type => type && type.trim() !== ''))];
    const queryWords = searchQuery.toLowerCase().split(/\s+/);
    let matchedSimulatorTypes = [];
    
    // Enhanced simulator type matching
    for (const simulatorType of availableSimulatorTypes) {
      const typeLower = simulatorType.toLowerCase();
      const typeWords = typeLower.split(/\s+/);
      
      let hasMatch = false;
      for (const searchWord of queryWords) {
        // Skip common words
        if (['usage', 'sessions', 'data', 'show', 'me', 'in', 'the', 'for', 'with', 'mentor'].includes(searchWord)) {
          continue;
        }
        
        // Check for matches including abbreviations
        for (const typeWord of typeWords) {
          if (typeWord === searchWord || 
              (searchWord.length >= 3 && typeWord.includes(searchWord)) ||
              (typeWord.length >= 3 && searchWord.includes(typeWord)) ||
              // Special handling for common abbreviations
              (searchWord === 'angio' && typeLower.includes('angio')) ||
              (searchWord === 'lap' && typeLower.includes('lap')) ||
              (searchWord === 'gi' && typeLower.includes('gi')) ||
              (searchWord === 'arthro' && typeLower.includes('arthro')) ||
              advancedFuzzyMatch(typeWord, searchWord, 0.8)) {
            hasMatch = true;
            break;
          }
        }
        if (hasMatch) break;
      }
      
      if (hasMatch) {
        matchedSimulatorTypes.push(simulatorType);
        console.log(`✅ Country query - matched simulator: ${simulatorType}`);
      }
    }
    
    if (matchedSimulatorTypes.length === 0) {
      console.log('❌ No simulator type detected');
      return null;
    }
    
    console.log(`✅ Found simulator types: ${matchedSimulatorTypes.join(', ')}`);
    
    // Now detect country
    const matchingSites = findCountryMatches(searchQuery);
    
    if (matchingSites.length === 0) {
      console.log('❌ No country detected');
      return null;
    }
    
    const countryName = matchingSites[0].country;
    console.log(`✅ Found country: ${countryName} with ${matchingSites.length} sites`);
    
    // Filter simulation stats for this simulator type + country combination
    const countrySiteNames = matchingSites.map(site => site.siteName);
    const filteredStats = simulationStats.filter(stat => 
      matchedSimulatorTypes.includes(stat.simulatorType) && 
      countrySiteNames.includes(stat.siteName)
    );
    
    console.log(`🎯 Found ${filteredStats.length} ${matchedSimulatorTypes.join(', ')} records in ${countryName}`);
    
    return {
      simulatorTypes: matchedSimulatorTypes,
      sites: matchingSites,
      stats: filteredStats,
      country: countryName
    };
  };
  const findSimulatorRegionalMatches = (searchQuery: string) => {
    console.log(`🎯 Checking for simulator + region combination in: "${searchQuery}"`);
    
    // First, detect simulator types with improved precision
    const availableSimulatorTypes = [...new Set(simulationStats.map(stat => stat.simulatorType).filter(type => type && type.trim() !== ''))];
    const queryWords = searchQuery.toLowerCase().split(/\s+/);
    let matchedSimulatorTypes = [];
    
    // Enhanced simulator type matching (similar to findSimulatorTypeMatches)
    for (const simulatorType of availableSimulatorTypes) {
      const typeLower = simulatorType.toLowerCase();
      const typeWords = typeLower.split(/\s+/);
      
      let hasMatch = false;
      for (const searchWord of queryWords) {
        // Skip common words
        if (['usage', 'sessions', 'data', 'show', 'me', 'in', 'the', 'for', 'with', 'mentor'].includes(searchWord)) {
          continue;
        }
        
        // Check for matches including abbreviations
        for (const typeWord of typeWords) {
          if (typeWord === searchWord || 
              (searchWord.length >= 3 && typeWord.includes(searchWord)) ||
              (typeWord.length >= 3 && searchWord.includes(typeWord)) ||
              // Special handling for common abbreviations
              (searchWord === 'angio' && typeLower.includes('angio')) ||
              (searchWord === 'lap' && typeLower.includes('lap')) ||
              (searchWord === 'gi' && typeLower.includes('gi')) ||
              (searchWord === 'arthro' && typeLower.includes('arthro')) ||
              advancedFuzzyMatch(typeWord, searchWord, 0.8)) {
            hasMatch = true;
            break;
          }
        }
        if (hasMatch) break;
      }
      
      if (hasMatch) {
        matchedSimulatorTypes.push(simulatorType);
        console.log(`✅ Regional query - matched simulator: ${simulatorType}`);
      }
    }
    
    if (matchedSimulatorTypes.length === 0) {
      console.log('❌ No simulator type detected');
      return null;
    }
    
    console.log(`✅ Found simulator types: ${matchedSimulatorTypes.join(', ')}`);
    
    // Now detect region
    const region = ['europe', 'apac', 'asia pacific', 'north america', 'na'].find(r => searchQuery.includes(r));
    
    if (!region) {
      console.log('❌ No region detected - returning global simulator data');
      // Return global simulator data for the SPECIFIC simulators only
      const filteredStats = simulationStats.filter(stat => 
        matchedSimulatorTypes.includes(stat.simulatorType)
      );
      const siteNames = [...new Set(filteredStats.map(stat => stat.siteName))];
      const matchingSites = siteData.filter(site => siteNames.includes(site.siteName));
      
      return { 
        simulatorTypes: matchedSimulatorTypes, 
        sites: matchingSites, 
        stats: filteredStats,
        region: 'global'
      };
    }
    
    console.log(`✅ Found region: ${region}`);
    
    // Get regional sites
    const regionalSites = getRegionalSites(region, siteData);
    console.log(`📍 Found ${regionalSites.length} sites in ${region}`);
    
    if (regionalSites.length === 0) {
      return null;
    }
    
    // Filter simulation stats for this simulator type + region combination
    const regionalSiteNames = regionalSites.map(site => site.siteName);
    const filteredStats = simulationStats.filter(stat => 
      matchedSimulatorTypes.includes(stat.simulatorType) && 
      regionalSiteNames.includes(stat.siteName)
    );
    
    console.log(`🎯 Found ${filteredStats.length} ${matchedSimulatorTypes.join(', ')} records in ${region}`);
    
    return {
      simulatorTypes: matchedSimulatorTypes,
      sites: regionalSites,
      stats: filteredStats,
      region: region
    };
  };
  
  // Helper functions (keep existing ones)
  const findCountryMatches = (searchTerm: string) => {
    console.log(`🌍 Searching for country matches in: "${searchTerm}"`);
    
    const matchingSites = siteData.filter(site => {
      const siteCountry = (site.country || '').toLowerCase();
      const searchLower = searchTerm.toLowerCase();
      
      // Direct country name match
      if (siteCountry.includes(searchLower) || searchLower.includes(siteCountry)) {
        console.log(`✅ Country match: ${site.siteName} in ${site.country}`);
        return true;
      }
      
      // Handle common country variations
      const countryVariations = {
        'usa': ['united states', 'america', 'us'],
        'united states': ['usa', 'america', 'us'],
        'america': ['usa', 'united states', 'us'],
        'uk': ['united kingdom', 'britain', 'england'],
        'united kingdom': ['uk', 'britain', 'england'],
        'britain': ['uk', 'united kingdom', 'england'],
        'korea': ['south korea'],
        'south korea': ['korea']
      };
      
      // Check if search term matches any country variations
      for (const [key, variations] of Object.entries(countryVariations)) {
        if (searchLower.includes(key) || key.includes(searchLower)) {
          if (variations.some(variation => 
              siteCountry.includes(variation) || 
              variation.includes(siteCountry)
          )) {
            console.log(`✅ Country variation match: ${site.siteName} in ${site.country} (${key})`);
            return true;
          }
        }
      }
      
      return false;
    });
    
    console.log(`🎯 Found ${matchingSites.length} sites for country search: "${searchTerm}"`);
    return matchingSites;
  };

  const createVisualSummary = (sites: SiteData[], type: string, stats?: any[], simulatorType?: string, region?: string) => {
    const onlineSites = sites.filter(s => s.isOnline);
    const totalSessions = sites.reduce((sum, s) => sum + s.numSessionsLastMonth, 0);
    
    let widgets = [];
    let charts = [];

    // Base widgets
    widgets.push(
      { icon: Server, label: "Total Sites", value: sites.length, color: "text-blue-500" },
      { icon: CheckCircle, label: "Online Sites", value: onlineSites.length, subValue: `${Math.round(onlineSites.length/sites.length*100)}%`, color: "text-green-500" },
      { icon: Activity, label: "Total Sessions", value: totalSessions, subValue: "Last Month", color: "text-purple-500" },
      { icon: Users, label: "Active Users", value: sites.reduce((sum, s) => sum + s.activeUsers, 0), color: "text-orange-500" }
    );

    // For simulator + region queries, add simulator-specific data
    if (stats && simulatorType && region) {
      const simSessionsLast12Months = stats.reduce((sum, stat) => sum + stat.sessionsLast12Months, 0);
      widgets.push({
        icon: Activity,
        label: `${simulatorType} Sessions`,
        value: simSessionsLast12Months,
        subValue: "Last 12 Months",
        color: "text-indigo-500"
      });

      // Create country breakdown for simulator usage
      const countrySimData = sites.reduce((acc, site) => {
        const country = site.country || 'Unknown';
        const siteSimStats = stats.filter(stat => stat.siteName === site.siteName);
        const siteSimSessions = siteSimStats.reduce((sum, stat) => sum + stat.sessionsLast12Months, 0);
        
        if (!acc[country]) {
          acc[country] = { sessions: 0, sites: 0 };
        }
        acc[country].sessions += siteSimSessions;
        if (siteSimSessions > 0) {
          acc[country].sites += 1;
        }
        return acc;
      }, {} as Record<string, { sessions: number; sites: number }>);

      // Create chart data for countries with simulator usage
      const countryChartData = Object.entries(countrySimData)
        .filter(([, data]) => data.sessions > 0)
        .map(([country, data]) => ({ 
          name: country.substring(0, 10), 
          value: data.sessions 
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 8); // Top 8 countries

      if (countryChartData.length > 1) {
        charts.push({ 
          type: 'bar', 
          data: countryChartData, 
          title: `${simulatorType} Usage by Country in ${region.charAt(0).toUpperCase() + region.slice(1)} (Last 12 Months)` 
        });
      }
    }

    // Standard charts for other query types
    const statusData = [
      { name: 'Online', value: onlineSites.length },
      { name: 'Offline', value: sites.length - onlineSites.length }
    ];
    if (statusData[1].value > 0) {
      charts.push({ type: 'pie', data: statusData, title: 'Site Status Distribution' });
    }

    if (type !== 'customer' && sites.length > 1 && !simulatorType) {
      const customerData = sites.reduce((acc, site) => {
        const customer = site.customer.substring(0, 15);
        acc[customer] = (acc[customer] || 0) + site.numSessionsLastMonth;
        return acc;
      }, {} as Record<string, number>);

      const topCustomers = Object.entries(customerData)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([name, value]) => ({ name, value }));

      if (topCustomers.length > 1) {
        charts.push({ type: 'bar', data: topCustomers, title: 'Top Customers by Sessions' });
      }
    }

    return { widgets, charts };
  };
  
  // Helper function to generate intelligent suggestions
  const generateIntelligentSuggestions = (originalQuery: string, foundSites: SiteData[]) => {
    const suggestions = [];
    
    if (foundSites.length === 0) {
      // Find similar entities in the same region/country
      const words = originalQuery.toLowerCase().split(/\s+/);
      const potentialCountries = ['france', 'usa', 'japan', 'germany', 'canada', 'australia', 'uk'];
      const potentialRegions = ['europe', 'apac', 'north america'];
      
      // Suggest similar countries
      potentialCountries.forEach(country => {
        const countrySites = findCountryMatches(country);
        if (countrySites.length > 0) {
          suggestions.push({
            query: `${country} usage`,
            reason: `Try ${country} - ${countrySites.length} sites available`
          });
        }
      });
      
      // Suggest regional alternatives
      potentialRegions.forEach(region => {
        const regionalSites = getRegionalSites(region, siteData);
        if (regionalSites.length > 0) {
          suggestions.push({
            query: `${region} overview`,
            reason: `Try ${region} region - ${regionalSites.length} sites available`
          });
        }
      });
      
      // Suggest similar time periods
      if (originalQuery.includes('month')) {
        suggestions.push({
          query: originalQuery.replace('month', 'year'),
          reason: 'Try yearly data instead'
        });
      }
      
      // Suggest general alternatives
      suggestions.push(
        { query: 'show online sites', reason: 'View all active sites' },
        { query: 'europe usage by country', reason: 'Regional breakdown' }
      );
    }
    
    return suggestions.slice(0, 3);
  };
  
  // STEP 1: Analyze Intent
  console.log(`🎯 Query Intent Analysis Result:`, intent);
  console.log(`Intent type: ${intent.type}, Entity found: ${intent.detectedEntity ? 'Yes' : 'No'}`);
  if (intent.detectedEntity) {
    console.log(`Entity details:`, intent.detectedEntity);
  }
  
  // STEP 2: Execute based on Intent Priority
  await new Promise(resolve => setTimeout(resolve, 800));
  
  // PRIORITY 1: Check for SIMULATOR + REGION combination (e.g., "Angio mentor usage in Europe")
  const simulatorRegionalMatch = findSimulatorRegionalMatches(query);
  if (simulatorRegionalMatch) {
    const { simulatorTypes, sites, stats, region } = simulatorRegionalMatch;
    const totalSessions = stats.reduce((sum, stat) => sum + stat.sessionsLast12Months, 0);
    
    console.log(`🎯 Simulator + Region query: ${simulatorTypes.join(', ')} in ${region} - ${sites.length} sites, ${totalSessions} sessions`);
    
    if (totalSessions === 0) {
      return {
        queryId,
        answer: `**${simulatorTypes.join(', ')} in ${region.charAt(0).toUpperCase() + region.slice(1)}** - No usage found. The region has ${sites.length} sites but no ${simulatorTypes.join(', ')} usage data.`,
        relevantData: sites.slice(0, 5),
        suggestions: [
          { type: 'analysis', title: `${simulatorTypes[0]} Global Usage`, description: `View ${simulatorTypes[0]} usage worldwide` },
          { type: 'analysis', title: `${region} All Simulators`, description: `View all simulator usage in ${region}` }
        ]
      };
    }
    
    const visualSummary = createVisualSummary(sites, 'simulator-region', stats, simulatorTypes[0], region);
    
    // Group by country for detailed breakdown
    const countryBreakdown = sites.reduce((acc, site) => {
      const country = site.country || 'Unknown';
      const siteStats = stats.filter(stat => stat.siteName === site.siteName);
      const siteSessions = siteStats.reduce((sum, stat) => sum + stat.sessionsLast12Months, 0);
      
      if (!acc[country]) {
        acc[country] = { sites: [], sessions: 0, activeSites: 0 };
      }
      acc[country].sites.push(site);
      acc[country].sessions += siteSessions;
      if (siteSessions > 0) {
        acc[country].activeSites += 1;
      }
      return acc;
    }, {} as Record<string, { sites: SiteData[]; sessions: number; activeSites: number }>);
    
    const topCountries = Object.entries(countryBreakdown)
      .filter(([, data]) => data.sessions > 0)
      .sort(([, a], [, b]) => b.sessions - a.sessions)
      .slice(0, 5);
    
    let countryDetails = '';
    if (topCountries.length > 0) {
      countryDetails = '\n\n**Top Countries:**\n' + 
        topCountries.map(([country, data]) => 
          `• ${country}: ${data.sessions} sessions (${data.activeSites} active sites)`
        ).join('\n');
    }
    
    return {
      queryId,
      answer: `**${simulatorTypes.join(', ')} Usage in ${region.charAt(0).toUpperCase() + region.slice(1)}** - ${totalSessions} sessions across ${sites.length} sites (last 12 months)${countryDetails}`,
      relevantData: sites.slice(0, 8),
      visualSummary,
      suggestions: [
        { type: 'report', title: `${simulatorTypes[0]} ${region} Report`, description: `Generate detailed regional report` },
        { type: 'analysis', title: `Compare with Other Regions`, description: `Compare ${simulatorTypes[0]} usage globally` }
      ]
    };
  }
  
  // PRIORITY 1.5: Enhanced logic: Check for SIMULATOR + COUNTRY combination
  const simulatorCountryMatch = findSimulatorCountryMatches(inputQuery);
  if (simulatorCountryMatch) {
    const { simulatorTypes, sites, stats, country } = simulatorCountryMatch;
    const totalSessions = stats.reduce((sum, stat) => sum + stat.sessionsLast12Months, 0);
    
    console.log(`Simulator + Country query: Found ${simulatorTypes.join(', ')} in ${country} with ${sites.length} sites and ${totalSessions} sessions`);
    
    if (totalSessions === 0) {
      return {
        queryId,
        answer: `**${simulatorTypes.join(', ')} in ${country}** - No usage data found for the last 12 months. ${sites.length} sites available but no ${simulatorTypes.join(', ')} activity.`,
        relevantData: sites,
        suggestions: [
          { type: 'analysis', title: `${simulatorTypes[0]} Global Usage`, description: `View ${simulatorTypes[0]} usage worldwide` },
          { type: 'analysis', title: `${country} Overview`, description: `View all simulators in ${country}` }
        ]
      };
    }
    
    const visualSummary = createVisualSummary(sites, 'simulator-country', stats, simulatorTypes[0], country);
    
    // Create site breakdown for detailed response
    const siteBreakdown = sites.map(site => {
      const siteSimStats = stats.filter(stat => stat.siteName === site.siteName);
      const siteSimSessions = siteSimStats.reduce((sum, stat) => sum + stat.sessionsLast12Months, 0);
      return {
        site,
        sessions: siteSimSessions
      };
    }).filter(item => item.sessions > 0).sort((a, b) => b.sessions - a.sessions);
    
    let siteDetails = '';
    if (siteBreakdown.length > 0) {
      siteDetails = '\n\n**Active Sites:**\n' + 
        siteBreakdown.slice(0, 5).map(({site, sessions}) => 
          `• ${site.siteName}: ${sessions} sessions`
        ).join('\n');
    }
    
    return {
      queryId,
      answer: `**${simulatorTypes.join(', ')} Usage in ${country}** - ${totalSessions} sessions across ${siteBreakdown.length} active sites (last 12 months)${siteDetails}`,
      relevantData: siteBreakdown.map(item => item.site).slice(0, 8),
      visualSummary,
      suggestions: [
        { type: 'report', title: `${simulatorTypes[0]} ${country} Report`, description: `Generate detailed country report` },
        { type: 'analysis', title: `${country} All Simulators`, description: `View all simulators in ${country}` }
      ]
    };
  }
  
  // PRIORITY 2: Enhanced logic: Check for SITE + SIMULATOR combination
  const siteSimulatorMatch = checkSiteSimulatorCombination(query, siteData, simulationStats);
  if (siteSimulatorMatch.found) {
    const { site, simulatorTypes, stats, totalSessions, siteOnly } = siteSimulatorMatch;
    
    console.log(`Site ${siteOnly ? 'only' : '+ Simulator'} query: ${site.siteName} ${siteOnly ? '' : `with ${simulatorTypes.join(', ')} -`} ${totalSessions} sessions`);
    
    // Handle site-only queries (like "MSR usage" or "MSR status")
    if (siteOnly) {
      const visualSummary = createVisualSummary([site], 'site');
      
      // Get all simulation stats for this site to provide comprehensive info
      const siteSimStats = simulationStats.filter(stat => stat.siteName === site.siteName);
      const totalSiteSimSessions = siteSimStats.reduce((sum, stat) => sum + stat.sessionsLast12Months, 0);
      
      return {
        queryId,
        answer: `**${site.siteName}** (${site.customer}) - ${site.isOnline ? '🟢 Online' : '🔴 Offline'} | ${site.numSessionsLastMonth} sessions last month | ${site.activeUsers} active users${totalSiteSimSessions > 0 ? ` | ${totalSiteSimSessions} simulator sessions (12 months)` : ''}`,
        relevantData: [site],
        visualSummary,
        suggestions: [
          { type: 'report', title: `${site.siteName} Report`, description: 'Generate detailed site report' },
          { type: 'calendar', title: `Contact ${site.customer}`, description: 'Schedule check-up call' },
          { type: 'crm', title: 'Open in CRM', description: 'View in Dynamics CRM' }
        ]
      };
    }
    
    // Handle site + simulator queries
    if (totalSessions === 0) {
      return {
        queryId,
        answer: `**${site.siteName}** - No ${simulatorTypes.join(', ')} usage found. The site has other simulators available but no data for the requested type.`,
        relevantData: [site],
        suggestions: [
          { type: 'analysis', title: `${site.siteName} All Simulators`, description: `View all simulator usage at ${site.siteName}` },
          { type: 'analysis', title: `${simulatorTypes[0]} Global Usage`, description: `See ${simulatorTypes[0]} usage across all sites` },
          { type: 'report', title: `${site.siteName} Report`, description: 'Generate site report' }
        ]
      };
    }
    
    const visualSummary = createVisualSummary([site], 'site-simulator', stats, simulatorTypes[0]);
    
    return {
      queryId,
      answer: `**${site.siteName}** ${simulatorTypes.join(', ')} Usage - ${totalSessions} sessions (last 12 months)`,
      relevantData: [site],
      visualSummary,
      suggestions: [
        { type: 'report', title: `${site.siteName} ${simulatorTypes[0]} Report`, description: 'Generate detailed usage report' },
        { type: 'analysis', title: `${site.siteName} All Simulators`, description: 'View all simulators at this site' }
      ]
    };
  }
  
  // PRIORITY 3: INTENT: Simulator type queries (only if no specific site mentioned)
  const simulatorMatch = findSimulatorTypeMatches(query);
  if (simulatorMatch && !intent.detectedEntity?.site) {
    const { simulatorTypes, sites, stats } = simulatorMatch;
    const visualSummary = createVisualSummary(sites, 'simulator');
    
    const totalSessions = stats.reduce((sum, stat) => sum + stat.sessionsLast12Months, 0);
    
    console.log(`Simulator type query: Found ${simulatorTypes.join(', ')} with ${sites.length} sites and ${totalSessions} sessions`);
    
    return {
      queryId,
      answer: `**${simulatorTypes.join(', ')} Simulator Usage** - Found ${sites.length} sites with ${totalSessions} sessions (last 12 months)`,
      relevantData: sites,
      visualSummary,
      suggestions: [
        { type: 'report', title: `${simulatorTypes[0]} Report`, description: 'Generate detailed simulator report' },
        { type: 'analysis', title: `${simulatorTypes[0]} Analysis`, description: 'Analyze simulator performance' }
      ]
    };
  }
  
  // INTENT: Site-specific queries
  if (intent.type === 'site' && intent.detectedEntity?.site) {
    const site = intent.detectedEntity.site;
    const visualSummary = createVisualSummary([site], 'site');
    
    console.log(`Site query: Found site ${site.siteName}`);
    
    return {
      queryId,
      answer: `**${site.siteName}** (${site.customer}) - ${site.isOnline ? 'Online' : 'Offline'}`,
      relevantData: [site],
      visualSummary,
      suggestions: [
        { type: 'report', title: `${site.siteName} Report`, description: 'Generate detailed site report' },
        { type: 'calendar', title: `Contact ${site.customer}`, description: 'Schedule check-up call' },
        { type: 'crm', title: 'Open in CRM', description: 'View in Dynamics CRM' }
      ]
    };
  }
  
  // INTENT: Customer-specific queries
  if (intent.type === 'customer' && intent.detectedEntity?.site) {
    const detectedSite = intent.detectedEntity.site;
    const customerSites = siteData.filter(s => 
      s.customer.toLowerCase() === detectedSite.customer.toLowerCase()
    );
    
    console.log(`Customer query: Found ${customerSites.length} sites for ${detectedSite.customer}`);
    
    if (customerSites.length > 0) {
      const visualSummary = createVisualSummary(customerSites, 'customer');
      
      return {
        queryId,
        answer: `**${detectedSite.customer}** Customer Analysis - ${customerSites.length} sites`,
        relevantData: customerSites,
        visualSummary,
        suggestions: [
          { type: 'report', title: `${detectedSite.customer} Report`, description: 'Generate customer report' },
          { type: 'calendar', title: `Contact ${detectedSite.customer}`, description: 'Schedule customer call' },
          { type: 'crm', title: 'Open in CRM', description: 'View customer in CRM' }
        ]
      };
    }
  }
  
  // INTENT: Regional Breakdown (e.g., "Europe usage by country")
  if (intent.type === 'regionalBreakdown') {
    const region = ['europe', 'apac', 'asia pacific', 'north america', 'na'].find(r => query.includes(r));
    
    if (region) {
      const regionalSites = getRegionalSites(region, siteData);
      console.log(`Regional breakdown for ${region}: ${regionalSites.length} sites found`);
      
      if (regionalSites.length === 0) {
        return {
          queryId,
          answer: `No sites found in ${region}. Please check if there are sites in this region.`,
          relevantData: [],
          suggestions: []
        };
      }
      
      // Group by country for breakdown
      const countryBreakdown = regionalSites.reduce((acc, site) => {
        const country = site.country || 'Unknown';
        if (!acc[country]) {
          acc[country] = { sites: [], sessions: 0, users: 0 };
        }
        acc[country].sites.push(site);
        acc[country].sessions += site.numSessionsLastMonth;
        acc[country].users += site.activeUsers;
        return acc;
      }, {} as Record<string, { sites: SiteData[]; sessions: number; users: number }>);
      
      // Create country breakdown chart
      const countryChartData = Object.entries(countryBreakdown)
        .map(([country, data]) => ({ 
          name: country.substring(0, 10), 
          value: data.sessions 
        }))
        .sort((a, b) => b.value - a.value);
      
      const visualSummary = {
        widgets: [
          { icon: Globe, label: "Countries", value: Object.keys(countryBreakdown).length, color: "text-blue-500" },
          { icon: Server, label: "Total Sites", value: regionalSites.length, color: "text-green-500" },
          { icon: Activity, label: "Total Sessions", value: regionalSites.reduce((sum, s) => sum + s.numSessionsLastMonth, 0), color: "text-purple-500" },
          { icon: Users, label: "Active Users", value: regionalSites.reduce((sum, s) => sum + s.activeUsers, 0), color: "text-orange-500" }
        ],
        charts: [
          { type: 'bar', data: countryChartData, title: `Sessions by Country in ${region.charAt(0).toUpperCase() + region.slice(1)}` }
        ]
      };
      
      return {
        queryId,
        answer: `**${region.charAt(0).toUpperCase() + region.slice(1)} Usage by Country** - ${Object.keys(countryBreakdown).length} countries`,
        relevantData: regionalSites.slice(0, 8),
        visualSummary,
        suggestions: [
          { type: 'report', title: `${region} Country Report`, description: `Generate detailed breakdown by country` },
          { type: 'analysis', title: `${region} Performance Analysis`, description: `Compare country performance in ${region}` }
        ]
      };
    }
  }
  
  // INTENT: Country-specific queries
  if (intent.type === 'country') {
    const countryMatches = findCountryMatches(query);
    console.log(`Country query "${query}": found ${countryMatches.length} matches`);
    if (countryMatches.length > 0) {
      // Extract country name from the first matched site
      const firstSite = countryMatches[0];
      const countryName = firstSite.country;
      console.log(`Country matches for "${query}":`, countryMatches.map(s => `${s.siteName} - ${s.country}`));
      const visualSummary = createVisualSummary(countryMatches, 'country');
      
      console.log(`Country query: Found ${countryMatches.length} sites for ${countryName}`);
      
      return {
        queryId,
        answer: `**${countryName}** Overview - Found ${countryMatches.length} sites`,
        relevantData: countryMatches,
        visualSummary,
        suggestions: [
          { type: 'report', title: `${countryName} Report`, description: `Generate detailed report for ${countryName}` },
          { type: 'analysis', title: `${countryName} Analysis`, description: `Analyze performance in ${countryName}` }
        ]
      };
    }
  }
  
  // INTENT: Regional queries (overall region data)
  if (intent.type === 'regional') {
    const region = ['europe', 'apac', 'asia pacific', 'north america', 'na'].find(r => query.includes(r));
    
    if (region) {
      const regionalSites = getRegionalSites(region, siteData);
      console.log(`Regional query for ${region}: ${regionalSites.length} sites found`);
      
      if (regionalSites.length > 0) {
        const visualSummary = createVisualSummary(regionalSites, 'regional');
        
        return {
          queryId,
          answer: `**${region.charAt(0).toUpperCase() + region.slice(1)} Regional Overview**`,
          relevantData: regionalSites.slice(0, 5),
          visualSummary,
          suggestions: [
            { type: 'report', title: `${region} Regional Report`, description: `Generate comprehensive regional report` },
            { type: 'analysis', title: `${region} Performance Analysis`, description: `Analyze regional trends` }
          ]
        };
      }
    }
  }
  
  // No results found - Generate intelligent suggestions with site-specific alternatives
  const intelligentSuggestions = generateIntelligentSuggestions(inputQuery, []);
  
  // Add specific suggestions for available sites and simulators
  const siteSpecificSuggestions = [];
  
  // Suggest existing sites
  const topSites = siteData.filter(s => s.isOnline && s.numSessionsLastMonth > 0)
    .sort((a, b) => b.numSessionsLastMonth - a.numSessionsLastMonth)
    .slice(0, 3);
  
  topSites.forEach(site => {
    siteSpecificSuggestions.push({
      type: 'analysis' as const,
      title: `${site.siteName} usage`,
      description: `View ${site.customer} activity`
    });
  });
  
  // Suggest available simulator types
  const topSimulatorTypes = [...new Set(simulationStats.map(stat => stat.simulatorType))]
    .filter(type => type && type.trim() !== '')
    .slice(0, 2);
    
  topSimulatorTypes.forEach(simType => {
    siteSpecificSuggestions.push({
      type: 'analysis' as const,
      title: `${simType} global usage`,
      description: `View ${simType} usage across all sites`
    });
  });
  
  const allSuggestions = [...intelligentSuggestions, ...siteSpecificSuggestions].slice(0, 4);
  
  return {
    queryId,
    answer: `I couldn't find specific results for "${inputQuery}". I've checked for site name, region, customer, and simulator type combinations but found no exact match. Here are verified alternatives:`,
    relevantData: [],
    suggestions: allSuggestions
  };
};

export const AIAgent = () => {
  const { siteData, simulationStats } = useData();
  console.log(`AIAgent using ${siteData.length} sites from data source:`, siteData.slice(0, 3));
  
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState<AIResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [feedback, setFeedback] = useState<{[queryId: string]: { rating: 'positive' | 'negative', comment?: string }}>({});
  const [showFeedbackComment, setShowFeedbackComment] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string>(() => `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`);
  const [currentQuery, setCurrentQuery] = useState<string>('');
  
  // Site detail modal state
  const [selectedSite, setSelectedSite] = useState<SiteData | null>(null);
  const [showSiteDetail, setShowSiteDetail] = useState(false);

  // Handle site detail modal
  const handleSiteDetail = (site: SiteData) => {
    setSelectedSite(site);
    setShowSiteDetail(true);
  };

  // Generate site URL using the correct mentorlearn.com format
  const generateSiteUrl = (site: SiteData) => {
    return `https://${site.siteName.toLowerCase()}.mentorlearn.com`;
  };

  // Enhanced dynamic autocomplete suggestions based on actual data
  const getSuggestions = (input: string) => {
    if (input.length < 2) return [];
    
    const inputLower = input.toLowerCase();
    const suggestions = [];
    
    console.log(`🔍 Getting suggestions for input: "${inputLower}"`);
    
    // Enhanced site matching with better fuzzy matching
    siteData.forEach(site => {
      const siteName = site.siteName.toLowerCase();
      const customer = site.customer.toLowerCase();
      const siteWords = siteName.split(' ');
      const inputWords = inputLower.split(' ');
      
      // Check for partial site name matches (any word in input matches any word in site name)
      const siteMatches = siteWords.some(siteWord => 
        inputWords.some(inputWord => 
          siteWord.includes(inputWord) || 
          inputWord.includes(siteWord) ||
          (siteWord.length >= 2 && inputWord.length >= 2 && advancedFuzzyMatch(siteWord, inputWord, 0.7))
        )
      );
      
      if (siteMatches || siteName.includes(inputLower) || inputLower.includes(siteName)) {
        suggestions.push(`${site.siteName} usage status`);
        suggestions.push(`${site.siteName} activity report`);
        if (site.activeUsers > 0) {
          suggestions.push(`${site.siteName} user analytics`);
        }
        console.log(`✅ Added suggestions for site: ${site.siteName}`);
      }
      
      // Enhanced customer matching
      const customerWords = customer.split(' ');
      const customerMatches = customerWords.some(customerWord => 
        inputWords.some(inputWord => 
          customerWord.includes(inputWord) || 
          inputWord.includes(customerWord) ||
          (customerWord.length >= 3 && inputWord.length >= 3 && advancedFuzzyMatch(customerWord, inputWord, 0.8))
        )
      );
      
      if (customerMatches || customer.includes(inputLower) || inputLower.includes(customer)) {
        suggestions.push(`${site.customer} overview`);
        suggestions.push(`${site.customer} sites status`);
      }
    });
    
    // Enhanced country suggestions
    const countries = [...new Set(siteData.map(s => s.country).filter(c => c))];
    countries.forEach(country => {
      const countryLower = country.toLowerCase();
      if (countryLower.includes(inputLower) || inputLower.includes(countryLower) || 
          advancedFuzzyMatch(countryLower, inputLower, 0.8)) {
        suggestions.push(`${country} sites overview`);
        suggestions.push(`${country} usage summary`);
        suggestions.push(`${country} performance report`);
      }
    });
    
    // Enhanced region suggestions
    const regions = [...new Set(siteData.map(s => s.region).filter(r => r))];
    regions.forEach(region => {
      const regionLower = region.toLowerCase();
      if (regionLower.includes(inputLower) || inputLower.includes(regionLower) ||
          advancedFuzzyMatch(regionLower, inputLower, 0.8)) {
        suggestions.push(`${region} regional analysis`);
        suggestions.push(`${region} usage by country`);
      }
    });
    
    // Enhanced common query patterns with better matching
    const queryPatterns = [
      "offline sites status",
      "license renewals needed", 
      "top performing sites",
      "monthly usage trends",
      "active users summary",
      "site performance comparison",
      "regional breakdown",
      "customer activity overview"
    ];
    
    queryPatterns.forEach(pattern => {
      if (advancedFuzzyMatch(pattern, inputLower, 0.6) || 
          pattern.toLowerCase().includes(inputLower) ||
          inputLower.split(' ').some(word => pattern.toLowerCase().includes(word))) {
        suggestions.push(pattern);
      }
    });
    
    // Remove duplicates and return top 8 suggestions
    const uniqueSuggestions = [...new Set(suggestions)].slice(0, 8);
    console.log(`💡 Generated ${uniqueSuggestions.length} suggestions:`, uniqueSuggestions);
    return uniqueSuggestions;
  };

  const suggestions = useMemo(() => getSuggestions(query), [query]);

  // Initialize Supabase client with proper error handling
  const getSupabaseClient = () => {
    // Use the actual Supabase project credentials - VITE_* variables are not supported in Lovable
    const supabaseUrl = 'https://uadehwzumpktcvjfocej.supabase.co';
    const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVhZGVod3p1bXBrdGN2amZvY2VqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTY1NjEwOTQsImV4cCI6MjA3MjEzNzA5NH0.VcEPyVC5eH-CiP0V7859_cEs050HkTerNY2UWYTKXhU';
    
    try {
      return createClient(supabaseUrl, supabaseKey);
    } catch (error) {
      console.error('Error creating Supabase client:', error);
      return null;
    }
  };

  const processQuery = async (inputQuery: string) => {
    setIsLoading(true);
    setResponse(null);
    console.log(`🚀 Processing query: "${inputQuery}"`);
    console.log(`📊 Available site data count: ${siteData.length}`);
    console.log(`🎯 Sample sites:`, siteData.slice(0, 5).map(s => s.siteName));
    
    try {
      const supabase = getSupabaseClient();
      
      if (!supabase) {
        console.log('❌ No Supabase client - using local data processing fallback');
        console.log('🔄 Calling processQueryLocallyWithVisuals...');
        setCurrentQuery(inputQuery); // Store current query for feedback
        const fallbackResponse = await processQueryLocallyWithVisuals(inputQuery, siteData, simulationStats);
        console.log('📋 Local processing response:', fallbackResponse);
        setResponse(fallbackResponse);
        return;
      }

      // Sync operational knowledge periodically (every 6 hours)
      const lastSync = localStorage.getItem('lastKnowledgeSync');
      const now = new Date().getTime();
      const sixHours = 6 * 60 * 60 * 1000;
      
      if (!lastSync || (now - parseInt(lastSync)) > sixHours) {
        console.log('🔄 Syncing operational knowledge...');
        try {
          await supabase.functions.invoke('sync-operational-knowledge');
          localStorage.setItem('lastKnowledgeSync', now.toString());
          console.log('✅ Operational knowledge synced successfully');
        } catch (syncError) {
          console.warn('⚠️ Failed to sync operational knowledge:', syncError);
          // Continue with the query even if sync fails
        }
      }

      console.log('✅ Supabase client available - calling Enhanced AI Assistant');
      console.log('📤 Sending to Edge Function:', { query: inputQuery, siteDataCount: siteData.length });
      setCurrentQuery(inputQuery); // Store current query for feedback
      const { data, error } = await supabase.functions.invoke('enhanced-ai-assistant', {
        body: {
          query: inputQuery,
          siteData,
          simulationStats
        }
      });

      if (error) {
        console.error('Edge Function error:', error);
        throw error;
      }

      console.log('✅ Edge Function response received:', data);
      const aiResponse: AIResponse = {
        answer: data.answer,
        relevantData: data.relevantData || [],
        suggestions: (data.suggestions || []).map((suggestion: any) => ({
          ...suggestion,
          action: createActionHandler(suggestion, data.relevantData)
        })),
        usedWebSearch: data.usedWebSearch
      };

      console.log('🎯 Final AI response:', aiResponse);
      setResponse(aiResponse);
    } catch (error) {
      console.error('AI Assistant Error - falling back to local processing:', error);
      console.log('🔄 Calling local fallback...');
      setCurrentQuery(inputQuery); // Store current query for feedback
      
      const fallbackResponse = await processQueryLocallyWithVisuals(inputQuery, siteData, simulationStats);
      console.log('📋 Fallback response:', fallbackResponse);
      setResponse(fallbackResponse);
    } finally {
      setIsLoading(false);
      setQuery('');
    }
  };

  // Store feedback and process with AI learning system
  const submitFeedback = async (queryId: string, rating: 'positive' | 'negative', comment?: string, correctionText?: string) => {
    const localFeedback = { ...feedback, [queryId]: { rating, comment } };
    setFeedback(localFeedback);
    
    try {
      const supabase = getSupabaseClient();
      if (supabase) {
        // Use the enhanced feedback processor
        const feedbackData = {
          sessionId,
          userQuery: currentQuery,
          aiResponse: response?.answer || '',
          feedbackType: correctionText ? 'correction' : (rating === 'positive' ? 'thumbs_up' : 'thumbs_down'),
          feedbackValue: rating === 'positive' ? 1 : -1,
          feedbackComment: comment,
          correctionText: correctionText
        };

        const { data, error } = await supabase.functions.invoke('feedback-processor', {
          body: feedbackData
        });

        if (error) {
          throw error;
        }

        console.log('Feedback processed successfully:', data);
      } else {
        // Fallback to localStorage for demo
        const existingFeedback = JSON.parse(localStorage.getItem('ai_feedback') || '[]');
        existingFeedback.push({
          queryId,
          query: currentQuery,
          response: response?.answer || '',
          rating,
          comment,
          timestamp: Date.now()
        });
        localStorage.setItem('ai_feedback', JSON.stringify(existingFeedback));
        console.log('Feedback stored locally');
      }
    } catch (error) {
      console.error('Failed to process feedback:', error);
      // Fallback to localStorage
      const existingFeedback = JSON.parse(localStorage.getItem('ai_feedback') || '[]');
      existingFeedback.push({
        queryId,
        query: currentQuery,
        response: response?.answer || '',
        rating,
        comment,
        timestamp: Date.now()
      });
      localStorage.setItem('ai_feedback', JSON.stringify(existingFeedback));
    }
    
    setShowFeedbackComment(null);
  };

  // Enhanced Feedback component with correction capability
  const FeedbackSection = ({ queryId }: { queryId: string }) => {
    const [comment, setComment] = useState('');
    const [correctionMode, setCorrectionMode] = useState(false);
    const [correctionText, setCorrectionText] = useState('');
    const currentFeedback = feedback[queryId];
    
    if (currentFeedback) {
      return (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Thank you for your feedback!</span>
          {currentFeedback.rating === 'positive' ? (
            <ThumbsUp className="w-3 h-3 text-green-500" />
          ) : (
            <ThumbsDown className="w-3 h-3 text-red-500" />
          )}
          {currentFeedback.comment && (
            <span>• {currentFeedback.comment}</span>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-2 border-t pt-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Was this response helpful?</span>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => submitFeedback(queryId, 'positive')}
              className="h-7 px-2"
              title="Helpful response"
            >
              <ThumbsUp className="w-3 h-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFeedbackComment(queryId)}
              className="h-7 px-2"
              title="Needs improvement"
            >
              <ThumbsDown className="w-3 h-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFeedbackComment(queryId)}
              className="h-7 px-2"
              title="Add comment"
            >
              <MessageCircle className="w-3 h-3" />
            </Button>
          </div>
        </div>
        
        {showFeedbackComment === queryId && (
          <div className="space-y-2">
            <div className="flex gap-2 mb-2">
              <Button
                variant={!correctionMode ? "default" : "outline"}
                size="sm"
                onClick={() => setCorrectionMode(false)}
                className="h-7 px-2 text-xs"
              >
                Comment
              </Button>
              <Button
                variant={correctionMode ? "default" : "outline"}
                size="sm"
                onClick={() => setCorrectionMode(true)}
                className="h-7 px-2 text-xs"
              >
                Provide Correction
              </Button>
            </div>
            
            {!correctionMode ? (
              <Input
                placeholder="Share your feedback..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="text-xs"
              />
            ) : (
              <textarea
                placeholder="Provide the correct answer or improved response..."
                value={correctionText}
                onChange={(e) => setCorrectionText(e.target.value)}
                className="w-full p-2 text-xs border rounded resize-none"
                rows={3}
              />
            )}
            
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowFeedbackComment(null);
                  setComment('');
                  setCorrectionText('');
                  setCorrectionMode(false);
                }}
                className="h-7 px-2 text-xs"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  if (correctionMode && correctionText.trim()) {
                    submitFeedback(queryId, 'negative', 'User provided correction', correctionText);
                  } else if (comment.trim()) {
                    submitFeedback(queryId, 'negative', comment);
                  }
                  setComment('');
                  setCorrectionText('');
                  setCorrectionMode(false);
                }}
                className="h-7 px-2 text-xs"
              >
                Submit {correctionMode ? 'Correction' : 'Feedback'}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const createActionHandler = (suggestion: any, relevantData: any[]) => {
    return () => {
      switch (suggestion.type) {
        case 'report':
          generateReport(relevantData.length > 0 ? relevantData : siteData);
          break;
        case 'calendar':
          if (relevantData.length > 0) {
            scheduleCall(relevantData[0].customer);
          } else {
            scheduleCall('General');
          }
          break;
        case 'crm':
          if (relevantData.length > 0) {
            openDynamicsCRM(relevantData[0].customer);
          } else {
            openDynamicsCRM('');
          }
          break;
        case 'analysis':
          analyzeInactiveSites(relevantData.length > 0 ? relevantData : siteData);
          break;
        default:
          console.log('Unknown suggestion type:', suggestion.type);
      }
    };
  };

  const generateReport = (sites: SiteData[]) => {
    const reportData = sites.map(site => ({
      Site: site.siteName,
      Customer: site.customer,
      Status: site.isOnline ? 'Online' : 'Offline',
      'Sessions (Last Month)': site.numSessionsLastMonth,
      'Active Users': site.activeUsers,
      'License Expiry': site.siteLicenseExpiration
    }));
    
    const csvContent = [
      Object.keys(reportData[0]).join(','),
      ...reportData.map(row => Object.values(row).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mentorlearn-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const scheduleCall = (customer: string) => {
    const subject = encodeURIComponent(`MentorLearn Cloud check-up call`);
    const body = encodeURIComponent(`Hi,\n\nI hope this email finds you well. I wanted to reach out to schedule a check-up call regarding your MentorLearn usage and discuss any questions or feedback you might have.\n\nPlease let me know your availability for the coming week.\n\nBest regards`);
    
    const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
    window.open(mailtoUrl, '_blank');
  };

  const openDynamicsCRM = (customer: string) => {
    const dynamicsUrl = `https://surgicalscienceprod.crm4.dynamics.com/main.aspx?appid=66fefb0e-2cb2-ec11-9840-00224883c6a6&forceUCI=1&pagetype=entitylist&etn=account&viewid=00000000-0000-0000-00aa-000010001002&viewType=1039`;
    window.open(dynamicsUrl, '_blank');
  };

  const analyzeInactiveSites = (sites: SiteData[]) => {
    generateReport(sites);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      processQuery(query.trim());
    }
  };

  const handleClearChat = () => {
    setQuery('');
    setResponse(null);
    setFeedback({});
    setShowFeedbackComment(null);
    setShowSuggestions(false);
  };

  return (
    <Card className="shadow-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-primary" />
          AI Assistant
          {response?.usedWebSearch && (
            <Badge variant="secondary" className="text-xs flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              Web Enhanced
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <div className="flex-1 relative">
              <Input
                type="text"
                placeholder="Ask me anything... (e.g., 'Europe usage by country', 'show me APAC usage last year')"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setShowSuggestions(e.target.value.length > 1);
                }}
                onFocus={() => setShowSuggestions(query.length > 1)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                className="flex-1"
              />
              
              {/* Autocomplete Suggestions */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-background border rounded-md shadow-lg z-10 mt-1">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-muted text-sm"
                      onClick={() => {
                        setQuery(suggestion);
                        setShowSuggestions(false);
                        processQuery(suggestion);
                      }}
                    >
                      <Lightbulb className="w-3 h-3 inline mr-2" />
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <Button type="submit" disabled={isLoading || !query.trim()}>
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
            </Button>
            
            {(response || query.trim()) && (
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleClearChat}
                disabled={isLoading}
                title="Clear chat"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </form>
        </div>

        {response && (
          <div className="space-y-4">
            {/* AI Response */}
            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="flex items-start gap-2 mb-2">
                <Bot className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                <div className="text-sm whitespace-pre-line">{response.answer}</div>
              </div>
              {response.usedWebSearch && (
                <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Enhanced with current web information
                </div>
              )}
            </div>

            {/* Visual Summary */}
            {(response as any)?.visualSummary && (
              <div className="space-y-4">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Visual Summary
                </h4>
                
                {/* Stat Widgets */}
                {(response as any).visualSummary.widgets && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {(response as any).visualSummary.widgets.map((widget: any, index: number) => (
                      <StatWidget
                        key={index}
                        icon={widget.icon}
                        label={widget.label}
                        value={widget.value}
                        subValue={widget.subValue}
                        color={widget.color}
                      />
                    ))}
                  </div>
                )}
                
                {/* Charts */}
                {(response as any).visualSummary.charts && (response as any).visualSummary.charts.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(response as any).visualSummary.charts.map((chart: any, index: number) => (
                      <div key={index} className="p-3 bg-muted/20 rounded-lg">
                        {chart.type === 'bar' ? (
                          <MiniBarChart data={chart.data} title={chart.title} />
                        ) : (
                          <StatusPieChart data={chart.data} title={chart.title} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Relevant Data */}
            {response.relevantData && response.relevantData.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  Relevant Sites
                </h4>
                <div className="space-y-2">
                  {response.relevantData.map((site, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-md text-sm">
                      <div className="flex-1">
                        <div 
                          className="font-medium cursor-pointer hover:text-primary transition-colors underline"
                          onClick={() => handleSiteDetail(site)}
                          title={`Click to view ${site.siteName} details`}
                        >
                          {site.siteName}
                        </div>
                        <div className="text-muted-foreground">{site.customer} • {site.country}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          <a 
                            href={generateSiteUrl(site)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-primary transition-colors"
                          >
                            Visit Site →
                          </a>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={site.isOnline ? "default" : "destructive"}>
                          {site.isOnline ? (
                            <><CheckCircle className="w-3 h-3 mr-1" />Online</>
                          ) : (
                            <><AlertTriangle className="w-3 h-3 mr-1" />Offline</>
                          )}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {site.numSessionsLastMonth} sessions
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSiteDetail(site)}
                          className="h-7 px-2 text-xs"
                        >
                          View Status
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Suggestions */}
            {response.suggestions && response.suggestions.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Suggested Actions
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {response.suggestions.map((suggestion, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      onClick={suggestion.action}
                      className="flex items-center gap-2 h-auto p-3 text-left justify-start"
                    >
                      {suggestion.type === 'report' && <FileText className="w-4 h-4" />}
                      {suggestion.type === 'calendar' && <Calendar className="w-4 h-4" />}
                      {suggestion.type === 'crm' && <ExternalLink className="w-4 h-4" />}
                      {suggestion.type === 'analysis' && <TrendingUp className="w-4 h-4" />}
                      <div>
                        <div className="font-medium text-xs">{suggestion.title}</div>
                        <div className="text-xs text-muted-foreground">{suggestion.description}</div>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* User Feedback Section */}
            {response.queryId && (
              <FeedbackSection queryId={response.queryId} />
            )}
          </div>
        )}

        {/* Quick Examples */}
        {!response && !isLoading && (
          <div className="text-xs text-muted-foreground">
            <div className="mb-2 font-medium">Try asking:</div>
            <div className="space-y-1">
              <div>• "United States usage"</div>
              <div>• "Europe usage by country"</div>
              <div>• "Show me CCF status"</div>
              <div>• "HIAE performance"</div>
              <div>• "Cleveland Clinic usage"</div>
            </div>
          </div>
        )}
      </CardContent>
      
      {/* Site Detail Modal */}
      <SiteDetailModal
        site={selectedSite}
        isOpen={showSiteDetail}
        onClose={() => setShowSiteDetail(false)}
        simulationStats={simulationStats}
      />
    </Card>
  );
};
